import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Phone, Sparkles, Truck, Leaf, ShieldCheck, Check } from "lucide-react";
import { Reveal, MaskLine } from "@/components/site/Reveal";
import { BUSINESS, SERVICES } from "@/lib/site";

const HERO_IMG = "https://images.pexels.com/photos/5353890/pexels-photo-5353890.jpeg";
const CAR_IMG = "https://images.pexels.com/photos/5233271/pexels-photo-5233271.jpeg";
const DOG_IMG =
    "https://images.unsplash.com/photo-1761532631792-d9ffd5ae527a?crop=entropy&cs=srgb&fm=jpg&q=85";
const ECO_IMG = "https://images.pexels.com/photos/14643667/pexels-photo-14643667.jpeg";

const CHAPTERS = [
    {
        n: "01",
        title: "We come to you",
        body: "No schedules to remember, no hoses to drag out. On trash day we roll up right behind the truck and handle the dirty work.",
        icon: Truck,
    },
    {
        n: "02",
        title: "200° of pure clean",
        body: "High-pressure, high-heat water lifts grime, kills bacteria, and erases the smell — not just a rinse, a real sanitize.",
        icon: Sparkles,
    },
    {
        n: "03",
        title: "Kind to your yard",
        body: "Every drop is captured and disposed of responsibly. Eco-safe detergents keep pets, kids, and lawns happy.",
        icon: Leaf,
    },
];

export default function Home() {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

    return (
        <main data-testid="home-page" className="bg-sand">
            {/* HERO */}
            <section ref={heroRef} className="relative min-h-[100svh] grain overflow-hidden flex items-end">
                <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
                    <img src={HERO_IMG} alt="Suburban home" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-forestdeep/85 via-forest/45 to-forest/25" />
                </motion.div>

                <div className="relative z-10 mx-auto max-w-7xl w-full px-5 md:px-10 pb-16 md:pb-24 pt-32">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15 }}
                        className="mb-6 inline-flex items-center gap-2 rounded-full bg-sand/15 backdrop-blur px-4 py-2 text-sm text-sand border border-sand/20"
                    >
                        <span className="h-2 w-2 rounded-full bg-sky animate-pulse" />
                        Serving {BUSINESS.area}
                    </motion.div>

                    <h1 className="font-heading font-extrabold text-sand tracking-tight text-5xl sm:text-7xl lg:text-8xl leading-[0.92] max-w-4xl">
                        <MaskLine delay={0.1}>The freshest</MaskLine>
                        <MaskLine delay={0.24}>
                            bins on the <span className="text-sky">block.</span>
                        </MaskLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.7 }}
                        className="mt-7 max-w-xl text-base md:text-lg text-sand/85 leading-relaxed"
                    >
                        Professional trash-bin cleaning, dog-waste removal, and car washes — done at your
                        curb by {BUSINESS.owner}. Sanitized, deodorized, and eco-friendly every time.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.85 }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <Link
                            to="/book"
                            data-testid="hero-book-cta"
                            className="group inline-flex items-center gap-2 rounded-full bg-sand text-forest px-7 py-4 font-semibold hover:bg-sky transition-colors duration-200"
                        >
                            Book a clean
                            <ArrowUpRight size={19} className="group-hover:rotate-45 transition-transform duration-300" />
                        </Link>
                        <a
                            href={BUSINESS.phoneHref}
                            data-testid="hero-phone"
                            className="inline-flex items-center gap-2 rounded-full border border-sand/40 text-sand px-7 py-4 font-semibold hover:bg-sand/10 transition-colors duration-200"
                        >
                            <Phone size={17} /> {BUSINESS.phone}
                        </a>
                    </motion.div>
                </div>
            </section>

            {/* MARQUEE */}
            <div className="bg-forest text-sand py-5 border-y border-sand/10">
                <Marquee speed={45} gradient={false}>
                    {Array.from({ length: 4 }).map((_, i) => (
                        <span key={i} className="mx-12 font-heading text-lg md:text-2xl font-medium tracking-tight flex items-center gap-12">
                            Roseville
                            <span className="text-sky">✦</span> Greater Sacramento
                            <span className="text-sky">✦</span> Eco-Friendly Cleaning
                            <span className="text-sky">✦</span> Sanitized &amp; Deodorized
                            <span className="text-sky">✦</span>
                        </span>
                    ))}
                </Marquee>
            </div>

            {/* MANIFESTO CHAPTERS */}
            <section className="mx-auto max-w-7xl px-5 md:px-10 py-24 md:py-36">
                <Reveal>
                    <p className="text-sm uppercase tracking-[0.25em] text-forest/50 mb-4">How it works</p>
                    <h2 className="font-heading font-extrabold tracking-tight text-forest text-4xl md:text-6xl max-w-2xl leading-[0.95]">
                        A cleaner curb in three simple moves.
                    </h2>
                </Reveal>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
                    {CHAPTERS.map((c, i) => (
                        <Reveal key={c.n} delay={i * 0.12}>
                            <div className="group h-full rounded-3xl bg-white border border-black/5 p-8 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(14,56,42,0.12)] transition-[transform,box-shadow] duration-300">
                                <div className="flex items-center justify-between">
                                    <span className="font-heading text-5xl font-extrabold text-sage">{c.n}</span>
                                    <span className="h-12 w-12 rounded-2xl bg-forest text-sand flex items-center justify-center group-hover:bg-sky group-hover:text-forest transition-colors duration-300">
                                        <c.icon size={22} />
                                    </span>
                                </div>
                                <h3 className="mt-8 font-heading font-bold text-2xl text-forest">{c.title}</h3>
                                <p className="mt-3 text-forest/60 leading-relaxed">{c.body}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* SERVICES + PRICING BENTO */}
            <section id="pricing" className="bg-white border-y border-black/5">
                <div className="mx-auto max-w-7xl px-5 md:px-10 py-24 md:py-32">
                    <Reveal>
                        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                            <div>
                                <p className="text-sm uppercase tracking-[0.25em] text-forest/50 mb-4">Services &amp; pricing</p>
                                <h2 className="font-heading font-extrabold tracking-tight text-forest text-4xl md:text-6xl leading-[0.95]">
                                    Simple, honest rates.
                                </h2>
                            </div>
                            <p className="max-w-sm text-forest/60">
                                Pick a bin package, then stack on extras. No contracts, no surprise fees — pay per
                                service.
                            </p>
                        </div>
                    </Reveal>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                        {SERVICES.map((s, i) => {
                            const img = s.id === "car_wash" ? CAR_IMG : s.id === "dog_poop" ? DOG_IMG : ECO_IMG;
                            const span = s.featured ? "md:col-span-7" : s.id === "single_bin" ? "md:col-span-5" : "md:col-span-6";
                            return (
                                <Reveal key={s.id} delay={i * 0.08} className={span}>
                                    <div
                                        data-testid={`pricing-card-${s.id}`}
                                        className={`group relative h-full overflow-hidden rounded-3xl border p-8 flex flex-col justify-between min-h-[280px] hover:-translate-y-1 transition-transform duration-300 ${
                                            s.featured
                                                ? "bg-forest text-sand border-forest"
                                                : "bg-sand text-forest border-black/5"
                                        }`}
                                    >
                                        <div className="absolute -right-10 -top-10 h-44 w-44 rounded-full overflow-hidden opacity-70 group-hover:scale-110 transition-transform duration-500">
                                            <img src={img} alt={s.title} className="h-full w-full object-cover" />
                                        </div>
                                        <div className="relative">
                                            <span
                                                className={`text-xs uppercase tracking-widest ${
                                                    s.featured ? "text-sky" : "text-forest/50"
                                                }`}
                                            >
                                                {s.unit}
                                            </span>
                                            <h3 className="mt-2 font-heading font-bold text-2xl md:text-3xl max-w-[70%]">
                                                {s.title}
                                            </h3>
                                        </div>
                                        <div className="relative mt-6">
                                            <p className={`max-w-md text-sm leading-relaxed ${s.featured ? "text-sand/80" : "text-forest/60"}`}>
                                                {s.blurb}
                                            </p>
                                            <div className="mt-5 flex items-end gap-1">
                                                <span className="font-heading font-extrabold text-5xl tracking-tight">
                                                    ${s.price}
                                                </span>
                                                {s.unit === "add-on" && (
                                                    <span className={`mb-1.5 text-sm ${s.featured ? "text-sand/70" : "text-forest/50"}`}>
                                                        extra
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>

                    <Reveal delay={0.1}>
                        <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-forest/70">
                            {["Fully insured", "Eco-safe detergents", "Locally owned", "Satisfaction guaranteed"].map(
                                (f) => (
                                    <span key={f} className="flex items-center gap-2 text-sm font-medium">
                                        <Check size={16} className="text-forest" /> {f}
                                    </span>
                                ),
                            )}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SPLIT PROMISE */}
            <section className="mx-auto max-w-7xl px-5 md:px-10 py-24 md:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
                    <Reveal>
                        <div className="relative">
                            <div className="rounded-t-[999px] overflow-hidden aspect-[4/5] border border-black/5">
                                <img src={ECO_IMG} alt="Eco water clean" className="h-full w-full object-cover" />
                            </div>
                            <div className="absolute -bottom-6 -right-4 md:right-8 bg-forest text-sand rounded-2xl px-6 py-5 shadow-xl">
                                <p className="font-heading font-extrabold text-3xl">100%</p>
                                <p className="text-xs text-sage uppercase tracking-widest">Water reclaimed</p>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.12}>
                        <p className="text-sm uppercase tracking-[0.25em] text-forest/50 mb-4">The NextGen promise</p>
                        <h2 className="font-heading font-extrabold tracking-tight text-forest text-4xl md:text-5xl leading-[0.98]">
                            Your bins should be the cleanest thing on the curb.
                        </h2>
                        <p className="mt-6 text-forest/65 leading-relaxed">
                            Run by {BUSINESS.owner} right here in {BUSINESS.area}, NextGen treats every driveway
                            like it's our own. Show up, sanitize, and leave it fresher than you left it.
                        </p>
                        <div className="mt-8 space-y-4">
                            {[
                                { icon: ShieldCheck, t: "Sanitized to 99.9%", d: "Heat + pressure kills what a rinse can't." },
                                { icon: Leaf, t: "Zero runoff", d: "Every drop captured and disposed of safely." },
                            ].map((x) => (
                                <div key={x.t} className="flex gap-4">
                                    <span className="h-11 w-11 shrink-0 rounded-xl bg-sage/50 text-forest flex items-center justify-center">
                                        <x.icon size={20} />
                                    </span>
                                    <div>
                                        <p className="font-semibold text-forest">{x.t}</p>
                                        <p className="text-sm text-forest/55">{x.d}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Link
                            to="/book"
                            data-testid="promise-cta"
                            className="mt-9 inline-flex items-center gap-2 rounded-full bg-forest text-sand px-7 py-4 font-semibold hover:bg-forestdeep hover:-translate-y-0.5 transition-[background,transform] duration-200"
                        >
                            Schedule my first clean <ArrowUpRight size={18} />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
